"use client";

import React from "react";
import { ThermometerSnowflake, Refrigerator, Wrench, Factory, Zap, ArrowRight, Fan } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  title: "AI-COOLING",
  icon: ThermometerSnowflake,
  capabilities: ["SYS-01: Auto-Throttle", "PUE < 1.1 Efficiency", "Neural Thermal Maps", "Cryo-Optimization"],
  link: "/TechnicalSolutionPage"
}, {
  title: "DEMAND RESPONSE",
  icon: Refrigerator,
  capabilities: ["GRD-04: Peak Shaving", "VPP Integration", "Real-time Arbitrage", "Load Balancing"],
  link: "/TechnicalSolutionPage"
}, {
  title: "EXERGY RECOVERY",
  icon: Wrench,
  capabilities: ["REC-09: Waste Heat", "Liquid-to-Liquid", "District Heating", "ORC Systems"],
  link: "/TechnicalSolutionPage"
}, {
  title: "ASSET OPTIMIZATION",
  icon: Factory,
  capabilities: ["OPT-02: Predictive MTBF", "Digital Twin Sync", "Efficiency Audits", "Lifecycle Mgmt"],
  link: "/ControlCenterPage"
}, {
  title: "GRID INTELLIGENCE",
  icon: Zap,
  capabilities: ["NET-07: Frequency Res", "Ancillary Services", "Smart Metering", "Edge Compute"],
  link: "/ControlCenterPage"
}, {
  title: "THERMAL AUDIT",
  icon: Fan,
  capabilities: ["AUD-05: Leak ID", "Scope 1-3 Tracking", "Carbon Credits", "ROI Projection"],
  link: "/ExergyAuditPage"
}];
export default function PrecisionCoolingServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="33243" id="core-technical-capabilities" className="dark bg-[#0A0A0B] text-[#F5F5F7] py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#39FF14]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-slate-900/20 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className={`mb-16 max-w-3xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold tracking-[0.05em] mb-6 text-[#F5F5F7] font-sans uppercase">
            Core <span className="text-[#39FF14]">Technical</span> Capabilities
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed font-sans max-w-2xl">
            Exergy Systems deploys autonomic AI-thermal intelligence to eliminate energy waste. 
            Our surgical approach to exergy recovery ensures your facility operates at the theoretical limit of efficiency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => <Link key={i} to={service.link} className="block h-full" data-index={i}>
              <div className={cn("group relative h-full p-8 rounded-none border border-[#2A2A2E] bg-[#0A0A0B] backdrop-blur-md transition-all duration-300 ease-linear bg-[radial-gradient(circle,rgba(42,42,46,0.2)_1px,transparent_1px)] [background-size:20px_20px]", "hover:border-[#39FF14] hover:bg-[#0A0A0B] hover:shadow-[0_0_20px_rgba(57,255,20,0.1)]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                {}
                <div className="flex items-start justify-between mb-6">
                  <div className="p-3 rounded-none bg-[#39FF14]/5 border border-[#39FF14]/20 group-hover:bg-[#39FF14]/10 transition-colors duration-300">
                    <service.icon className="w-8 h-8 text-[#39FF14] group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  
                  {}
                  <div className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-[#39FF14]">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                </div>

                {}
                <h3 className="text-xl font-bold text-[#F5F5F7] mb-4 group-hover:text-[#39FF14] transition-colors duration-300 font-mono tracking-wider">
                  {service.title}
                </h3>

                {}
                <div className="space-y-2">
                  <div className="h-px w-12 bg-[#2A2A2E] mb-4 group-hover:w-full group-hover:bg-[#39FF14]/30 transition-all duration-500" />
                  <ul className="space-y-1.5">
                    {service.capabilities.map((cap, idx) => <li key={idx} className="flex items-center text-[12px] font-mono text-zinc-500 group-hover:text-[#39FF14] transition-colors">
                        <span className="w-1 h-1 bg-[#2A2A2E] mr-2 group-hover:bg-[#39FF14]" />
                        {cap}
                      </li>)}
                  </ul>
                </div>

                {}
                <div className="absolute bottom-0 right-0 w-0 h-0 border-b-[1px] border-r-[1px] border-transparent group-hover:border-[#39FF14] group-hover:w-4 group-hover:h-4 transition-all duration-300" />
              </div>
            </Link>)}
        </div>
      </div>
    </section>;
}